'use client'

import { useLiveVenuePayloads } from '@/service/venue-health.hook'
import { replayCachedIncrementalTickers } from '@/service/ws/incremental-ticker-cache'
import {
  createMultiStreamHook,
  createStreamHook,
  type MarketDataStreamPayload,
} from '@/service/ws/use-web-socket-with-proto'
import { IncrementalTickerList } from '@/types/generated/common/types'

const useRawIncrementalInstrumentStream = createStreamHook<
  MarketDataStreamPayload,
  IncrementalTickerList
>({
  channelKey: 'marketDataStream',
  extractResponse: (message, payload) => {
    const incrementalTickers = message.data?.incrementalTickers?.incrementalTickers.filter(
      (ticker) => ticker.symbol === payload.symbol && ticker.exchange === payload.exchange
    )
    return incrementalTickers?.length ? { incrementalTickers } : undefined
  },
  getPayloadDeps: (p) => [p.symbol, p.exchange, p.dataType],
  listenerChannelKey: 'marketDataStream:incrementalTickers',
  replay: replayCachedIncrementalTickers,
})

const useRawMultiIncrementalInstrumentStream = createMultiStreamHook<
  MarketDataStreamPayload,
  IncrementalTickerList
>({
  channelKey: 'marketDataStream',
  extractResponse: (message, payloads) => {
    const subscribedKeys = new Set(
      payloads.map((payload) => `${payload.exchange}:${payload.symbol}`)
    )
    const incrementalTickers = message.data?.incrementalTickers?.incrementalTickers.filter(
      (ticker) => subscribedKeys.has(`${ticker.exchange}:${ticker.symbol}`)
    )
    return incrementalTickers?.length ? { incrementalTickers } : undefined
  },
  listenerChannelKey: 'marketDataStream:incrementalTickers',
  replay: replayCachedIncrementalTickers,
})

export function useIncrementalInstrumentStream(
  options: Parameters<typeof useRawIncrementalInstrumentStream>[0]
) {
  const connected = options.connected ?? true
  const payloads = useLiveVenuePayloads([options.payload], connected)
  return useRawIncrementalInstrumentStream({
    ...options,
    connected: payloads.length > 0,
  })
}

export function useMultiIncrementalInstrumentStream(
  options: Parameters<typeof useRawMultiIncrementalInstrumentStream>[0]
) {
  const connected = options.connected ?? true
  const payloads = useLiveVenuePayloads(options.payloads, connected)
  return useRawMultiIncrementalInstrumentStream({
    ...options,
    connected: payloads.length > 0,
    payloads,
  })
}
